import { useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';

import { RoutePath } from '@/Routes/RoutePath';
import { actions, selectors } from '@/Store';
import { persistor } from '@/Store/configureStore';
import auth0Config from '@/Config/Auth0Config';
import { recordSignOut } from '@/Analytics/Auth';
import redirectToLogin from '@/Utils/redirectToLogin';

const getTokenSilentlyOptions = {
  audience: auth0Config.audience,
  cacheMode: 'on',
};

export const getUserContext = userInfo => userInfo?.[auth0Config.contextClaimNameSpace];
export const getUserProfiles = userInfo => userInfo?.[auth0Config.profileClaimNameSpace];
export const getUserActiveProfile = userInfo => getUserProfiles(userInfo)?.[0]; // currently we use the first as active
export const getSpotifyAccessToken = userInfo =>
  userInfo?.[auth0Config.spotifyClaimNameSpace]?.access_token;

const useAuth = () => {
  const dispatch = useDispatch();
  const {
    logout,
    loginWithRedirect,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    user,
    error,
  } = useAuth0();
  const authConfigFromBE = useSelector(selectors.auth.config);
  const authConfig: any = useMemo(
    () => (authConfigFromBE ? { ...auth0Config, ...authConfigFromBE } : auth0Config),
    [authConfigFromBE],
  );

  const signOut = useCallback(
    async (returnTo: string = RoutePath.home, silently = false) => {
      if (!silently) {
        recordSignOut(user?.email ?? '');
      }
      persistor.purge();
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}${returnTo}`,
        },
      });
    },
    [logout, user],
  );

  const loadToken = useCallback(
    async (ignoreCache = false) => {
      // https://github.com/auth0/auth0-react/blob/main/MIGRATION_GUIDE.md#ignorecache-on-getaccesstokensilently-has-been-removed-and-replace-with-cachemode
      // ignoreCache: true should be changed to use cacheMode: 'off'
      let token;
      try {
        const cacheMode = ignoreCache ? 'off' : 'on';
        token = await getAccessTokenSilently({ ...getTokenSilentlyOptions, cacheMode });
        // Set token in the store, to be able to retrieve account number in the getDecodeConfig selector
        dispatch(actions.auth.setAccessToken(token));
      } catch (err) {
        await signOut();
        redirectToLogin();
        throw err;
      }

      return token;
    },
    [getAccessTokenSilently, dispatch, signOut],
  );

  const login = useCallback(
    async (returnTo = RoutePath.home, errorDescription?) => {
      await loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          connection: authConfig?.connectionId,
          error_description: errorDescription,
        },
        openUrl: url => {
          window.location.replace(url);
        },
      });
    },
    [loginWithRedirect, authConfig],
  );

  const register = useCallback(
    async (returnTo = RoutePath.home) => {
      await loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          screen_hint: 'signup',
          connection: authConfig?.connectionId,
        },
        openUrl: url => {
          window.location.replace(url);
        },
      });
    },
    [loginWithRedirect, authConfig],
  );

  return {
    loadToken,
    login,
    register,
    signOut,
    isAuthenticated,
    authConfig,
    isLoading,
    error,
  };
};

export default useAuth;
